import { useState } from "react";
import { Link, usePage } from "@inertiajs/react";
import {
  HomeIcon,
  ChartBarIcon,
  RocketLaunchIcon,
  MegaphoneIcon,
  UsersIcon,
  Cog8ToothIcon,
  ArrowRightStartOnRectangleIcon,
  BookOpenIcon,
  PuzzlePieceIcon,
} from "@heroicons/react/24/outline";
import { twMerge } from "tailwind-merge";
import { NewProjectButton } from "./NewProjectButton";
import { WorkspaceSwitcher } from "./WorkspaceSwitcher";
import { useCurrentAccount } from "~/stores/sessionStore";

interface NavItem {
  label: string;
  href: string;
  icon: React.ElementType;
}

const baseNavItems: NavItem[] = [
  { label: "Home", href: "/", icon: HomeIcon },
  { label: "Websites", href: "/projects", icon: RocketLaunchIcon },
  { label: "Ads", href: "/ads", icon: MegaphoneIcon },
  { label: "Analytics", href: "/dashboard", icon: ChartBarIcon },
  { label: "Leads", href: "/leads", icon: UsersIcon },
];

export default function AppSidebar() {
  const { url, props } = usePage<{ integrations_enabled?: boolean }>();
  const [isCollapsed, setIsCollapsed] = useState(() => url !== "/");
  const currentAccount = useCurrentAccount();
  const isAdmin = currentAccount?.is_admin ?? false;
  const integrationsEnabled = props.integrations_enabled ?? false;

  const navItems: NavItem[] = integrationsEnabled
    ? [
        ...baseNavItems,
        { label: "Integrations", href: "/integrations", icon: PuzzlePieceIcon },
      ]
    : baseNavItems;

  const isActive = (href: string) => {
    if (href === "/") {
      return url === "/";
    }
    if (href === "/dashboard") {
      return url === "/dashboard";
    }
    if (href === "/projects") {
      return url.startsWith("/projects");
    }
    if (href === "/ads") {
      return url.startsWith("/ads");
    }
    if (href === "/leads") {
      return url.startsWith("/leads");
    }
    if (href === "/support") {
      return url === "/support";
    }
    if (href === "/integrations") {
      return url.startsWith("/integrations");
    }
    return url.startsWith(href);
  };

  return (
    <aside
      style={{
        backgroundColor: "#12183d",
        width: isCollapsed ? 72 : 300,
        minWidth: isCollapsed ? 72 : 300,
      }}
      className="h-screen flex flex-col transition-all duration-300 sticky top-0 shrink-0"
    >
      {/* Workspace switcher (includes collapse button when expanded) */}
      <WorkspaceSwitcher
        isCollapsed={isCollapsed}
        onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
      />

      {/* Collapse button (only when collapsed) */}
      {isCollapsed && (
        <div className="p-4 flex justify-center">
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="text-white hover:opacity-70 transition-opacity"
          >
            <ArrowRightStartOnRectangleIcon className="w-6 h-6" />
          </button>
        </div>
      )}

      {/* Divider below collapse button */}
      <div
        className="h-px"
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.15)",
          marginLeft: "1px",
          marginRight: "1px",
        }}
      />

      {/* New Project button */}
      <div className={twMerge("p-6", isCollapsed && "p-4 flex justify-center")}>
        <NewProjectButton isCollapsed={isCollapsed} />
      </div>

      {/* Navigation items */}
      <nav className="flex-1 px-1">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.href);

          return (
            <Link
              key={item.href}
              href={item.href}
              className={twMerge(
                "flex items-center gap-3 px-6 py-3 font-sans relative transition-colors",
                active ? "text-secondary-500" : "text-white hover:bg-white/10",
                isCollapsed && "justify-center px-0"
              )}
            >
              <Icon className="w-6 h-6 shrink-0" strokeWidth={1.5} />
              {!isCollapsed && <span className={active ? "font-semibold" : ""}>{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      {/* Divider above Settings & Support */}
      <div
        className="h-px"
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.15)",
          marginLeft: "1px",
          marginRight: "1px",
        }}
      />

      {/* Help Center */}
      <div className={twMerge("px-1", isCollapsed && "flex justify-center")}>
        <Link
          href="/support"
          className={twMerge(
            "flex items-center gap-3 px-6 py-3 font-sans relative transition-colors",
            isActive("/support") ? "text-secondary-500" : "text-white hover:bg-white/10",
            isCollapsed && "justify-center px-0"
          )}
        >
          <BookOpenIcon className="w-6 h-6 shrink-0" strokeWidth={1.5} />
          {!isCollapsed && <span className={isActive("/support") ? "font-semibold" : ""}>Help Center</span>}
        </Link>
      </div>

      {/* Settings (admin only) */}
      {isAdmin && (
        <div className={twMerge("px-1 pb-6", isCollapsed && "flex justify-center pb-4")}>
          <Link
            href="/settings"
            className={twMerge(
              "flex items-center gap-3 px-6 py-3 font-sans relative transition-colors",
              "text-white hover:bg-white/10",
              isCollapsed && "justify-center px-0"
            )}
          >
            <Cog8ToothIcon className="w-6 h-6 shrink-0" strokeWidth={1.5} />
            {!isCollapsed && <span>Settings</span>}
          </Link>
        </div>
      )}
    </aside>
  );
}
